// Public "KELTA Group Health Insurance" campaign section for the home page.
// Fully driven by the admin-managed campaign config (GET /api/insurance/campaign):
// heading, description, banner, button labels, deadline, instructions, on/off.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { HeartPulse, LogIn, Info, CalendarClock, ShieldCheck, Users, FileCheck } from "lucide-react";

export default function InsuranceSection() {
  const [c, setC] = useState(null);
  const [knowMore, setKnowMore] = useState(false);

  useEffect(() => {
    api.get("/insurance/campaign").then(r => setC(r.data)).catch(() => setC({ enabled: false }));
  }, []);

  if (!c || !c.enabled) return null;

  return (
    <section id="group-insurance" className="relative overflow-hidden text-white" style={{ background: "linear-gradient(135deg, #04202c 0%, #065a66 45%, #0d8a96 100%)" }} data-testid="group-insurance-section">
      {c.banner_image && (
        <img src={c.banner_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
      )}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(94,234,212,0.14),transparent_55%)]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-300">
              <HeartPulse className="w-3.5 h-3.5" /> Member Welfare
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mt-4 leading-tight" data-testid="gi-heading">
              {c.heading}
              <span className="block text-sm sm:text-base font-semibold tracking-wide text-cyan-300/90 mt-2 italic" data-testid="gi-tagline">
                Together We Protect. Together We Secure.
              </span>
            </h2>
            <p className="text-cyan-50/85 mt-4 max-w-xl text-sm sm:text-base leading-relaxed" data-testid="gi-description">
              {c.description}
            </p>
            <div className="flex flex-wrap gap-3 mt-7">
              {c.closed ? (
                <span className="inline-flex items-center gap-2 rounded-lg bg-red-500/20 border border-red-400/30 px-5 py-3 font-bold text-red-200" data-testid="gi-closed-pill">
                  <CalendarClock className="w-4 h-4" /> Data Collection Closed
                </span>
              ) : (
                <Link to="/insurance">
                  <Button size="lg" className="bg-cyan-400 hover:bg-cyan-300 text-cyan-950 font-bold shadow-lg shadow-cyan-400/25" data-testid="gi-member-login-btn">
                    <LogIn className="w-4 h-4 mr-2" /> {c.button_text || "Member Login"}
                  </Button>
                </Link>
              )}
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 bg-transparent" onClick={() => setKnowMore(true)} data-testid="gi-know-more-btn">
                <Info className="w-4 h-4 mr-2" /> {c.know_more_button_text || "Know More"}
              </Button>
            </div>
            {c.last_date && (
              <p className={`mt-4 text-xs font-semibold inline-flex items-center gap-1.5 ${c.closed ? "text-red-300" : "text-cyan-200"}`} data-testid="gi-last-date">
                <CalendarClock className="w-3.5 h-3.5" />
                {c.closed ? "The last date for data submission has passed." : `Submit your details before ${c.last_date.split("-").reverse().join("-")}`}
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-3">
            {[
              { icon: Users, t: "Family Covered", d: "Spouse, Father, Mother, Son(s), Daughter(s)" },
              { icon: FileCheck, t: "Simple Form", d: "Auto-filled KELTA details · save draft & continue later" },
              { icon: ShieldCheck, t: "Secure", d: "Data used only for the group policy quotation" },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} className="rounded-xl bg-white/[0.07] border border-white/10 backdrop-blur-sm p-4 flex lg:items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-cyan-400/15 text-cyan-300 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-bold text-sm">{t}</div>
                  <div className="text-xs text-cyan-100/70 mt-0.5">{d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={knowMore} onOpenChange={setKnowMore}>
        <DialogContent className="max-w-lg" data-testid="gi-know-more-dialog">
          <DialogHeader>
            <DialogTitle>{c.heading} — Instructions</DialogTitle>
            <DialogDescription>
              Instructions and eligibility information for the KELTA Group Health Insurance data collection.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">{c.know_more}</div>
          {!c.closed && (
            <Link to="/insurance">
              <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white" data-testid="gi-know-more-login">
                <LogIn className="w-4 h-4 mr-2" /> {c.button_text || "Member Login"}
              </Button>
            </Link>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
